import React, { useState, useEffect, useContext, createContext } from "react";
import Select from "react-select";
import { customStyles } from "../../../configs/Data";
import { toast } from "react-toastify";
import {
    fetchedProductsWithTariffs,
    sendOrderPurchaseData,
} from "../../../apis/apis.logistics/apisProduct";

const inputClass =
    "w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors";
const labelClass =
    "block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1";

export function NewOrderPurchaseOperationDetailForm({
    order,
    setOrder,
    selectedOption,
    setSelectedOption,
    initialOptionState,
}) {
    const [options, setOptions] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    // Calcular el costo anterior mostrado según la condición IGV actual
    const getDisplayPricePurchase = () => {
        const basePrice = selectedOption.pricePurchase; // Este es el precio base SIN IGV almacenado
        if (order.conditionIgv === "01") {
            // Si IGV incluido, mostrar precio con IGV
            return (basePrice * 1.18).toFixed(2);
        } else {
            // Si IGV aparte, mostrar precio sin IGV
            return basePrice.toFixed(2);
        }
    };
    // Función para obtener el tooltip del costo
    const getCostTooltip = (isNewCost = false) => {
        const conditionIgv = order.conditionIgv;

        if (isNewCost) {
            if (conditionIgv === "01") {
                return "El precio que ingreses aquí ya incluye el 18% de IGV. El sistema calculará automáticamente la base imponible.";
            } else {
                return "El precio que ingreses aquí NO incluye IGV. El sistema sumará el 18% automáticamente.";
            }
        } else {
            return "Este es el precio base registrado en el sistema, convertido según la condición IGV actual.";
        }
    };

    // =========================================================================
    // FUNCIÓN PARA OBTENER EL TEXTO DE AYUDA DEL COSTO
    // =========================================================================
    const getCostHint = (isNewCost = false) => {
        const conditionIgv = order.conditionIgv;

        if (isNewCost) {
            // Para "Costo Nuevo"
            if (conditionIgv === "01") {
                return {
                    text: "El precio INCLUYE IGV (18%)",
                    icon: "fas fa-check-circle",
                    color: "text-green-600 dark:text-green-400",
                };
            } else {
                return {
                    text: "El precio NO incluye IGV (se sumará aparte)",
                    icon: "fas fa-plus-circle",
                    color: "text-orange-600 dark:text-orange-400",
                };
            }
        } else {
            // Para "Costo Ant."
            if (conditionIgv === "01") {
                return {
                    text: "Precio mostrado CON IGV (basado en costo anterior)",
                    icon: "fas fa-history",
                    color: "text-gray-500 dark:text-gray-400",
                };
            } else {
                return {
                    text: "Precio mostrado SIN IGV (basado en costo anterior)",
                    icon: "fas fa-history",
                    color: "text-gray-500 dark:text-gray-400",
                };
            }
        }
    };
    const getProductsWithTariffs = async () => {
        try {
            const response = await fetchedProductsWithTariffs();

            // Filtrar productos que tengan al menos una tarifa "CJ"
            const filteredProducts = response.filter((product) => {
                // Verificar que el producto tenga tarifas
                if (
                    !product.productTariff ||
                    product.productTariff.length === 0
                ) {
                    return false;
                }

                // Verificar que al menos UNA tarifa tenga unitName "CJ"
                const hasAtLeastOneCJ = product.productTariff.some(
                    (tariff) => tariff.unitName === "CJ",
                );

                // Verificar que al menos tenga una tarifa
                const hasAtLeastOneTariff = product.productTariff.length > 0;

                return hasAtLeastOneCJ && hasAtLeastOneTariff;
            });

            // Usar un Map para evitar duplicados por productID
            const productMap = new Map();
            filteredProducts.forEach((mobile) => {
                if (!productMap.has(mobile["productID"])) {
                    productMap.set(mobile["productID"], {
                        value: mobile["productID"],
                        label:
                            mobile["productCode"] +
                            " - " +
                            mobile["productSku"] +
                            " - " +
                            mobile["purchaseName"] +
                            " - " +
                            mobile["productTariff"].length,
                        purchaseName: mobile["purchaseName"],
                        tariff: mobile["productTariff"],
                        grammage: mobile["productGrammage"],
                        sku: mobile["productSku"],
                        stockMax: mobile["productStockMax"],
                        stockMin: mobile["productStockMin"],
                    });
                }
            });

            // Convertir el Map de vuelta a un array y establecerlo en el estado
            setOptions(Array.from(productMap.values()));
        } catch (error) {
            throw error;
        }
    };
    const handleChangeProductBox = async (selectOption) => {
        // Si se limpia el selector (selectOption es null)
        if (!selectOption) {
            setSelectedProduct(null);
            setSelectedOption(initialOptionState);
            return;
        }

        // Guardar el producto seleccionado para controlar el Select
        setSelectedProduct(selectOption);

        // Filtrar solo las tarifas "CJ" del producto seleccionado
        const cjTariffs = selectOption.tariff.filter(
            (tariff) => tariff.unitName === "CJ",
        );

        // Si no hay tarifas CJ, usar la primera tarifa disponible
        const tariffsToUse =
            cjTariffs.length > 0 ? cjTariffs : selectOption.tariff;

        // =========================================================================
        // OBTENER PRECIO BASE (ALMACENADO EN BD - SIEMPRE SIN IGV)
        // =========================================================================
        const basePriceWithoutIgv = Number(tariffsToUse[0].pricePurchase);

        // Obtener conditionIgv desde order
        const conditionIgv = order.conditionIgv;

        // Obtener cantidad (si no hay, usar 0)
        const quantity = Number(selectedOption.quantityOrdered) || 0;

        // =========================================================================
        // CONVERTIR PRECIO SEGÚN CONDICIÓN IGV ACTUAL
        // =========================================================================
        let unitPrice; // Precio unitario a mostrar (con/sin IGV según condición)
        let grossAmount;
        let discountAmount;
        let netAmount;
        let taxableAmount;
        let igvAmount;
        let totalWithIgv;

        if (conditionIgv === "01") {
            // =====================================================================
            // IGV INCLUIDO: el precio debe mostrar el valor CON IGV
            // =====================================================================
            unitPrice = basePriceWithoutIgv * 1.18; // Base × 1.18 = precio con IGV

            // Monto bruto (quantity × unitPrice) - INCLUYE IGV
            grossAmount = unitPrice * quantity;

            // Descuento
            discountAmount =
                (grossAmount * Number(tariffsToUse[0].percentageDiscount)) /
                100;

            // Monto neto después del descuento (INCLUYE IGV)
            netAmount = grossAmount - discountAmount;

            // Separar base imponible del IGV
            taxableAmount = netAmount / 1.18;
            igvAmount = netAmount - taxableAmount;
            totalWithIgv = netAmount; // Ya incluye IGV
        } else {
            // =====================================================================
            // IGV APARTE: el precio debe mostrar el valor SIN IGV
            // =====================================================================
            unitPrice = basePriceWithoutIgv; // Base sin IGV directo

            // Monto bruto (quantity × unitPrice) - SIN IGV
            grossAmount = unitPrice * quantity;

            // Descuento
            discountAmount =
                (grossAmount * Number(tariffsToUse[0].percentageDiscount)) /
                100;

            // Monto neto después del descuento (SIN IGV)
            netAmount = grossAmount - discountAmount;

            // Base imponible = netAmount (sin IGV)
            taxableAmount = netAmount;
            igvAmount = taxableAmount * 0.18;
            totalWithIgv = netAmount + igvAmount;
        }

        // Determinar si es producto gratuito
        const isFree = selectOption.activeType === "02";
        const taxType = isFree ? "GRATUITO" : "GRAVADO";

        // =========================================================================
        // ACTUALIZAR selectedOption CON LOS VALORES CALCULADOS
        // =========================================================================
        setSelectedOption({
            ...selectedOption,
            // =====================================================================
            // DATOS DEL PRODUCTO
            // =====================================================================
            tariffs: selectOption.tariff,
            productID: selectOption.value,
            productName: selectOption.purchaseName,
            grammage: selectOption.grammage,
            sku: selectOption.sku,
            productStockMax: selectOption.stockMax,
            productStockMin: selectOption.stockMin,

            // =====================================================================
            // PRECIOS DESDE TARIFA
            // =====================================================================
            pricePurchase: basePriceWithoutIgv, // Guardar el precio BASE sin IGV
            priceSale: tariffsToUse[0].priceSale,
            unitaryPriceSale: tariffsToUse[0].unitaryPriceSale,
            percentageDiscount: tariffsToUse[0].percentageDiscount,

            // =====================================================================
            // UNIDAD
            // =====================================================================
            productTariffID: tariffsToUse[0].productTariffID,
            unitName: tariffsToUse[0].unitName,
            unitID: tariffsToUse[0].unitID,

            // =====================================================================
            // CAMPOS DE MONTO
            // =====================================================================
            unitPrice: unitPrice.toFixed(6), // Precio unitario (con/sin IGV según condición)
            grossAmount: grossAmount.toFixed(2), // Monto bruto (quantity × unitPrice)
            discountAmount: discountAmount.toFixed(2), // Monto del descuento
            netAmount: netAmount.toFixed(2), // Monto después del descuento

            // =====================================================================
            // CAMPOS FISCALES
            // =====================================================================
            taxableAmount: taxableAmount.toFixed(2), // Base imponible SIN IGV
            igvAmount: igvAmount.toFixed(2), // IGV calculado
            totalWithIgv: totalWithIgv.toFixed(2), // Total con IGV

            // =====================================================================
            // CAMPOS PARA GRATUITOS
            // =====================================================================
            isFree: isFree,
            taxType: taxType,
            freeAmount: isFree ? netAmount.toFixed(2) : 0,
            freeIgvAmount: 0,

            // =====================================================================
            // CANTIDADES
            // =====================================================================
            quantityOrdered: selectedOption.quantityOrdered,
            quantity: selectedOption.quantityOrdered,

            // =====================================================================
            // TRAZABILIDAD
            // =====================================================================
            expireDate: selectedOption.expireDate,
            condition: "01",
            conditionIgv: conditionIgv, // Guardar la condición actual en el item
        });
    };

    const handleChangeUnitBox = async (e) => {
        const found = await selectedOption.tariffs.find(function (post, index) {
            if (post.productTariffID == e.target.value) return true;
        });
        if (!found) return;

        const basePrice = Number(found.pricePurchase);
        let unitPrice = basePrice;

        let grossAmount,
            discountAmount,
            netAmount,
            taxableAmount,
            igvAmount,
            totalWithIgv;
        const conditionIgv = order.conditionIgv;
        const quantity = Number(selectedOption.quantityOrdered) || 0;

        if (conditionIgv === "01") {
            unitPrice = basePrice;
            grossAmount = unitPrice * quantity;
            discountAmount =
                (grossAmount * Number(found.percentageDiscount)) / 100;
            netAmount = grossAmount - discountAmount;
            taxableAmount = netAmount / 1.18;
            igvAmount = netAmount - taxableAmount;
            totalWithIgv = netAmount;
        } else {
            unitPrice = basePrice;
            grossAmount = unitPrice * quantity;
            discountAmount =
                (grossAmount * Number(found.percentageDiscount)) / 100;
            netAmount = grossAmount - discountAmount;
            taxableAmount = netAmount;
            igvAmount = taxableAmount * 0.18;
            totalWithIgv = netAmount + igvAmount;
        }

        const isFree = selectedOption.isFree;
        const taxType = selectedOption.taxType;

        setSelectedOption({
            ...selectedOption,
            pricePurchase: found.pricePurchase,
            priceSale: found.priceSale,
            unitaryPriceSale: found.unitaryPriceSale,
            unitPrice: unitPrice > 0 ? unitPrice.toFixed(6) : "0.000000",
            grossAmount: grossAmount.toFixed(2),
            discountAmount: discountAmount.toFixed(2),
            netAmount: netAmount.toFixed(2),
            productTariffID: found.productTariffID,
            percentageDiscount: found.percentageDiscount,
            unitName: found.unitName,
            taxableAmount: taxableAmount.toFixed(2),
            igvAmount: igvAmount.toFixed(2),
            totalWithIgv: totalWithIgv.toFixed(2),
            freeAmount: isFree ? netAmount.toFixed(2) : 0,
        });
    };

    const handleChangePriceAndQuantityBox = (e) => {
        let grossAmount,
            discountAmount,
            netAmount,
            taxableAmount,
            igvAmount,
            totalWithIgv,
            newUnitPrice;
        const conditionIgv = order.conditionIgv;
        const quantity =
            Number(
                e.target.name === "quantityOrdered"
                    ? e.target.value
                    : selectedOption.quantityOrdered,
            ) || 0;
        const discountPercent =
            Number(
                e.target.name === "percentageDiscount"
                    ? e.target.value
                    : selectedOption.percentageDiscount,
            ) || 0;

        if (e.target.name === "unitPrice") {
            const inputUnitPrice = Number(e.target.value) || 0;
            grossAmount = quantity * inputUnitPrice;
            discountAmount = (grossAmount * discountPercent) / 100;
            netAmount = grossAmount - discountAmount;

            if (conditionIgv === "01") {
                taxableAmount = netAmount / 1.18;
                igvAmount = netAmount - taxableAmount;
                totalWithIgv = netAmount;
            } else {
                taxableAmount = netAmount;
                igvAmount = taxableAmount * 0.18;
                totalWithIgv = netAmount + igvAmount;
            }

            setSelectedOption({
                ...selectedOption,
                unitPrice: e.target.value,
                grossAmount: grossAmount.toFixed(2),
                discountAmount: discountAmount.toFixed(2),
                netAmount: netAmount.toFixed(2),
                taxableAmount: taxableAmount.toFixed(2),
                igvAmount: igvAmount.toFixed(2),
                totalWithIgv: totalWithIgv.toFixed(2),
                freeAmount: selectedOption.isFree ? netAmount.toFixed(2) : 0,
            });
        } else if (e.target.name === "quantityOrdered") {
            const inputQuantity = Number(e.target.value) || 0;
            const currentUnitPrice = Number(selectedOption.unitPrice) || 0;
            grossAmount = inputQuantity * currentUnitPrice;
            discountAmount = (grossAmount * discountPercent) / 100;
            netAmount = grossAmount - discountAmount;

            if (conditionIgv === "01") {
                taxableAmount = netAmount / 1.18;
                igvAmount = netAmount - taxableAmount;
                totalWithIgv = netAmount;
            } else {
                taxableAmount = netAmount;
                igvAmount = taxableAmount * 0.18;
                totalWithIgv = netAmount + igvAmount;
            }

            setSelectedOption({
                ...selectedOption,
                quantityOrdered: e.target.value,
                quantity: e.target.value,
                grossAmount: grossAmount.toFixed(2),
                discountAmount: discountAmount.toFixed(2),
                netAmount: netAmount.toFixed(2),
                taxableAmount: taxableAmount.toFixed(2),
                igvAmount: igvAmount.toFixed(2),
                totalWithIgv: totalWithIgv.toFixed(2),
                freeAmount: selectedOption.isFree ? netAmount.toFixed(2) : 0,
            });
        } else if (e.target.name === "grossAmount") {
            const inputGrossAmount = Number(e.target.value) || 0;
            if (quantity > 0 && inputGrossAmount > 0) {
                newUnitPrice = inputGrossAmount / quantity;
            } else {
                newUnitPrice = 0;
            }

            discountAmount = (inputGrossAmount * discountPercent) / 100;
            netAmount = inputGrossAmount - discountAmount;

            if (conditionIgv === "01") {
                taxableAmount = netAmount / 1.18;
                igvAmount = netAmount - taxableAmount;
                totalWithIgv = netAmount;
            } else {
                taxableAmount = netAmount;
                igvAmount = taxableAmount * 0.18;
                totalWithIgv = netAmount + igvAmount;
            }

            setSelectedOption({
                ...selectedOption,
                grossAmount: e.target.value,
                unitPrice: newUnitPrice > 0 ? newUnitPrice.toFixed(6) : "",
                discountAmount: discountAmount.toFixed(2),
                netAmount: netAmount.toFixed(2),
                taxableAmount: taxableAmount.toFixed(2),
                igvAmount: igvAmount.toFixed(2),
                totalWithIgv: totalWithIgv.toFixed(2),
                freeAmount: selectedOption.isFree ? netAmount.toFixed(2) : 0,
            });
        } else if (e.target.name === "percentageDiscount") {
            const currentGrossAmount = Number(selectedOption.grossAmount) || 0;
            discountAmount = (currentGrossAmount * discountPercent) / 100;
            netAmount = currentGrossAmount - discountAmount;

            if (conditionIgv === "01") {
                taxableAmount = netAmount / 1.18;
                igvAmount = netAmount - taxableAmount;
                totalWithIgv = netAmount;
            } else {
                taxableAmount = netAmount;
                igvAmount = taxableAmount * 0.18;
                totalWithIgv = netAmount + igvAmount;
            }

            setSelectedOption({
                ...selectedOption,
                percentageDiscount: e.target.value,
                discountAmount: discountAmount.toFixed(2),
                netAmount: netAmount.toFixed(2),
                taxableAmount: taxableAmount.toFixed(2),
                igvAmount: igvAmount.toFixed(2),
                totalWithIgv: totalWithIgv.toFixed(2),
                freeAmount: selectedOption.isFree ? netAmount.toFixed(2) : 0,
            });
        }
    };

    const handleToggleFree = (checked) => {
        const netAmount = Number(selectedOption.netAmount) || 0;
        setSelectedOption({
            ...selectedOption,
            isFree: checked,
            taxType: checked ? "GRATUITO" : "GRAVADO",
            freeAmount: checked ? netAmount.toFixed(2) : 0,
        });
    };

    const handleButtonAdd = async (e) => {
        if (Number(selectedOption.productTariffID) > 0) {
            // Se permite repetir el mismo producto/tarifa si una línea es gratuita y la otra no
            // (caso "compra con bonificación": N unidades cobradas + 1 unidad gratis del mismo producto).
            const found = await order.operationItems.find(
                function (detail, index) {
                    if (
                        detail.productTariffID ==
                            selectedOption.productTariffID &&
                        Boolean(detail.isFree) ===
                            Boolean(selectedOption.isFree)
                    )
                        return true;
                },
            );

            if (!found) {
                if (Number(selectedOption.quantityOrdered) > 0) {
                    if (
                        selectedOption.unitPrice.toString().length > 0 &&
                        Number(selectedOption.unitPrice) >= 0
                    ) {
                        setOrder({
                            ...order,
                            operationItems: [
                                ...order.operationItems,
                                selectedOption,
                            ],
                        });
                        setSelectedOption(initialOptionState);
                        setSelectedProduct(null); // Limpiar el selector de producto
                    } else {
                        toast.info("verifique el nuevo costo");
                    }
                } else {
                    toast.info("verifique la cantidad");
                }
            } else {
                toast.info("Ya existe este item");
            }
        } else {
            toast.info("Elija un item");
        }
    };

    useEffect(() => {
        getProductsWithTariffs();
    }, []);
    return (
        <>
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden mb-4">
                <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h6 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <i className="fas fa-plus-circle text-blue-500"></i>
                        Agregar Producto
                    </h6>
                </div>
                <div className="p-5">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
                        <div className="md:col-span-5">
                            <label className={labelClass}>
                                <i className="fas fa-box mr-1"></i>
                                Producto
                            </label>
                            {options.length ? (
                                <Select
                                    options={options}
                                    value={selectedProduct}
                                    styles={customStyles}
                                    classNamePrefix="custom-react-select"
                                    onChange={(e) => handleChangeProductBox(e)}
                                    placeholder="Seleccione un producto..."
                                    isSearchable={true}
                                    isClearable={true}
                                    noOptionsMessage={() =>
                                        "No se encontraron productos"
                                    }
                                    filterOption={(option, inputValue) => {
                                        // Normalizar texto (minúsculas, eliminar espacios extra)
                                        const normalizeText = (text) =>
                                            text
                                                .toLowerCase()
                                                .replace(/\s+/g, " ")
                                                .trim();

                                        const normalizedInput =
                                            normalizeText(inputValue);
                                        const normalizedLabel = normalizeText(
                                            option.label,
                                        );
                                        const normalizedPurchaseName =
                                            normalizeText(
                                                option.purchaseName || "",
                                            );
                                        const normalizedSku = normalizeText(
                                            option.sku || "",
                                        );

                                        return (
                                            normalizedLabel.includes(
                                                normalizedInput,
                                            ) ||
                                            normalizedPurchaseName.includes(
                                                normalizedInput,
                                            ) ||
                                            normalizedSku.includes(
                                                normalizedInput,
                                            )
                                        );
                                    }}
                                />
                            ) : (
                                <div className="flex items-center justify-center py-2">
                                    <div className="h-5 w-5 rounded-full border-2 border-gray-300 border-t-blue-600 animate-spin mr-2"></div>
                                    <span className="text-sm text-gray-500 dark:text-gray-400">
                                        Cargando productos...
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="md:col-span-3">
                            <label className={labelClass}>
                                <i className="fas fa-cube mr-1"></i>
                                Unidad
                            </label>
                            <select
                                className={inputClass}
                                name="productTariffID"
                                id="productTariffID"
                                disabled={
                                    selectedOption.tariffs.length ? false : true
                                }
                                value={selectedOption.productTariffID}
                                onChange={(e) => handleChangeUnitBox(e)}
                            >
                                <option value="">Seleccione unidad...</option>
                                {selectedOption.tariffs.map((item, index) => {
                                    return (
                                        <option
                                            key={index}
                                            value={item.productTariffID}
                                        >
                                            {item.unitName}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                        <div className="md:col-span-4">
                            <label className={labelClass}>
                                <i className="fas fa-calendar mr-1"></i>
                                Fecha Vencimiento
                            </label>
                            <input
                                type="date"
                                name="expireDate"
                                className={inputClass}
                                value={selectedOption.expireDate}
                                onChange={(e) => {
                                    setSelectedOption({
                                        ...selectedOption,
                                        expireDate: e.target.value,
                                    });
                                }}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-4 mb-4">
                        <div className="sm:col-span-1">
                            <label className={labelClass}>
                                <i className="fas fa-money-bill-wave mr-1"></i>
                                Costo Ant.
                                <span className="ml-1 text-gray-400 dark:text-gray-500">
                                    <i
                                        className={`${getCostHint(false).icon} text-xs`}
                                    ></i>
                                </span>
                            </label>
                            <div className="flex items-center rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden group">
                                <div className="bg-gray-100 dark:bg-gray-700 px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
                                    S/
                                </div>
                                <input
                                    type="number"
                                    className={
                                        inputClass +
                                        " border-0 rounded-none w-full bg-gray-50 dark:bg-gray-700/50"
                                    }
                                    name="pricePurchase"
                                    placeholder="0.00"
                                    value={getDisplayPricePurchase()}
                                    readOnly
                                    onWheel={(e) => e.target.blur()}
                                    title={getCostHint(false).text}
                                />
                            </div>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 flex items-center gap-1">
                                <i className={getCostHint(false).icon}></i>
                                <span>{getCostHint(false).text}</span>
                            </p>
                        </div>
                        {/* discount */}
                        <div className="sm:col-span-1">
                            <label className={labelClass}>
                                <i className="fas fa-percentage mr-1"></i>
                                Dcto %
                            </label>
                            <input
                                type="number"
                                className={inputClass + " text-center"}
                                id="percentageDiscount"
                                name="percentageDiscount"
                                placeholder="0"
                                value={selectedOption.percentageDiscount}
                                onFocus={(e) => e.target.select()}
                                onWheel={(e) => e.target.blur()}
                                maxLength={6}
                                autoComplete="off"
                                onChange={(e) =>
                                    handleChangePriceAndQuantityBox(e)
                                }
                            />
                        </div>
                        {/* quantity */}
                        <div className="sm:col-span-1">
                            <label className={labelClass}>
                                <i className="fas fa-shopping-cart mr-1"></i>
                                Cantidad
                            </label>
                            <input
                                type="number"
                                className={inputClass + " text-center"}
                                name="quantityOrdered"
                                placeholder="0"
                                value={selectedOption.quantityOrdered}
                                onFocus={(e) => e.target.select()}
                                onWheel={(e) => e.target.blur()}
                                onChange={(e) =>
                                    handleChangePriceAndQuantityBox(e)
                                }
                                maxLength={6}
                                autoComplete="off"
                            />
                        </div>
                        {/* subtotal */}
                        <div className="sm:col-span-1">
                            <label className={labelClass}>
                                <i className="fas fa-calculator mr-1"></i>
                                Subtotal
                            </label>
                            <div className="flex items-center rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                                <div className="bg-gray-100 dark:bg-gray-700 px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
                                    S/
                                </div>
                                <input
                                    type="number"
                                    step="0.01"
                                    className={
                                        inputClass +
                                        " border-0 rounded-none w-full"
                                    }
                                    name="grossAmount"
                                    placeholder="0.00"
                                    value={selectedOption.grossAmount || ""}
                                    onFocus={(e) => e.target.select()}
                                    onWheel={(e) => e.target.blur()}
                                    onBlur={(e) => {
                                        const numericValue =
                                            Number(e.target.value) || 0;
                                        if (numericValue > 0) {
                                            setSelectedOption({
                                                ...selectedOption,
                                                grossAmount:
                                                    numericValue.toFixed(2),
                                            });
                                        }
                                    }}
                                    onChange={(e) =>
                                        handleChangePriceAndQuantityBox(e)
                                    }
                                    maxLength={10}
                                    autoComplete="off"
                                />
                            </div>
                        </div>
                        <div className="sm:col-span-2">
                            <label
                                className={labelClass}
                                title={getCostTooltip(true)}
                            >
                                <i className="fas fa-tag mr-1"></i>
                                Costo Nuevo
                                <span
                                    className={`ml-1 ${order.conditionIgv === "01" ? "text-green-600 dark:text-green-400" : "text-orange-600 dark:text-orange-400"}`}
                                >
                                    <i className="fas fa-info-circle text-xs"></i>
                                </span>
                            </label>
                            <div className="flex items-center rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                                <div className="bg-gray-100 dark:bg-gray-700 px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
                                    S/
                                </div>
                                <input
                                    type="number"
                                    step="0.000001"
                                    className={
                                        inputClass +
                                        " border-0 rounded-none w-full"
                                    }
                                    name="unitPrice"
                                    placeholder="0.00"
                                    value={selectedOption.unitPrice}
                                    onFocus={(e) => e.target.select()}
                                    onWheel={(e) => e.target.blur()}
                                    onChange={(e) =>
                                        handleChangePriceAndQuantityBox(e)
                                    }
                                    maxLength={10}
                                    autoComplete="off"
                                />
                            </div>
                            <div className="flex items-center justify-between mt-1">
                                <p
                                    className={`text-xs flex items-center gap-1 ${order.conditionIgv === "01" ? "text-green-600 dark:text-green-400" : "text-orange-600 dark:text-orange-400"}`}
                                >
                                    <i
                                        className={
                                            order.conditionIgv === "01"
                                                ? "fas fa-check-circle"
                                                : "fas fa-plus-circle"
                                        }
                                    ></i>
                                    <span>{getCostHint(true).text}</span>
                                </p>
                                {selectedOption.unitPrice &&
                                    Number(selectedOption.unitPrice) > 0 && (
                                        <p className="text-xs text-gray-400 dark:text-gray-500">
                                            {order.conditionIgv === "01" ? (
                                                <>
                                                    Base sin IGV: S/{" "}
                                                    {(
                                                        Number(
                                                            selectedOption.unitPrice,
                                                        ) / 1.18
                                                    ).toFixed(4)}
                                                </>
                                            ) : (
                                                <>
                                                    + IGV 18%: S/{" "}
                                                    {(
                                                        Number(
                                                            selectedOption.unitPrice,
                                                        ) * 0.18
                                                    ).toFixed(4)}
                                                </>
                                            )}
                                        </p>
                                    )}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-between">
                        <label className="inline-flex items-center gap-2 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                                checked={selectedOption.isFree}
                                onChange={(e) =>
                                    handleToggleFree(e.target.checked)
                                }
                            />
                            <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
                                <i className="fas fa-gift mr-1"></i>
                                Es gratuito / bonificación
                            </span>
                        </label>
                        <button
                            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-sm font-medium px-4 py-2 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onClick={handleButtonAdd}
                            disabled={!selectedOption.productTariffID}
                        >
                            <i className="fas fa-plus"></i>
                            Agregar Item
                        </button>
                    </div>
                    {selectedOption.isFree && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                            <i className="fas fa-info-circle mr-1"></i>
                            El costo declarado se registrará como valor de
                            referencia (para SUNAT/costeo), pero no sumará al
                            total a pagar al proveedor.
                        </p>
                    )}
                </div>
            </div>
        </>
    );
}
